/**
 * Sound Alert Constants
 * 
 * Centralized constants for sound alert configuration.
 * All magic numbers are extracted here for maintainability and clarity.
 */

export const SOUND_CONSTANTS = {
  // ============================================
  // Volume Configuration
  // ============================================
  
  /** Default global volume (0.0 to 1.0) */
  DEFAULT_VOLUME: 0.8,
  
  /** Default volume for alerts tab (higher for urgency) */
  DEFAULT_ALERTS_VOLUME: 0.9,
  
  /** Default volume for other tabs */
  DEFAULT_TAB_VOLUME: 0.8,
  
  /** Fallback volume when tab settings are missing */
  FALLBACK_TAB_VOLUME: 0.7,
  
  /** Minimum volume value (0.0) */
  MIN_VOLUME: 0,
  
  /** Maximum volume value (1.0) */
  MAX_VOLUME: 1,
  
  // ============================================
  // Volume Multipliers & Intensity
  // ============================================
  
  /** Base volume multiplier to prevent audio clipping (reduces base volume by 50%) */
  BASE_VOLUME_MULTIPLIER: 0.5,
  
  /** Intensity multiplier for alerts tab (50% louder for urgency) */
  ALERTS_INTENSITY_MULTIPLIER: 1.5,
  
  /** Default intensity for non-alert tabs */
  DEFAULT_INTENSITY: 1.0,
  
  // ============================================
  // Audio Thresholds
  // ============================================
  
  /** Minimum audible loudness level (prevents silent sounds) */
  MIN_LOUDNESS_THRESHOLD: 0.001,
  
  /** Gain ramp start time (seconds) - quick fade-in */
  GAIN_RAMP_START_TIME: 0.01,
  
  /** Gain exponential ramp end value (prevents audio pop) */
  GAIN_RAMP_END_VALUE: 0.001,
  
  // ============================================
  // Sound Frequencies (Hz)
  // ============================================
  
  /** Base frequency for new message sound */
  NEW_MESSAGE_FREQ: 700,
  
  /** Frequency step for second beep in new message sound */
  NEW_MESSAGE_FREQ_STEP: 150,
  
  /** Frequencies for new conversation sound (ascending beeps) */
  NEW_CONVERSATION_FREQS: [800, 1000, 1200],
  
  /** Frequency for urgent sound */
  URGENT_FREQ: 1000,

  /** Frequency for the review-conversation modal alarm */
  REVIEW_ALARM_FREQ: 1000,

  // ============================================
  // Sound Durations (seconds)
  // ============================================
  
  /** Duration of new message beep */
  NEW_MESSAGE_DURATION: 0.08,
  
  /** Duration of new conversation beep */
  NEW_CONVERSATION_DURATION: 0.12,
  
  /** Duration of urgent beep (shorter for urgency) */
  URGENT_DURATION: 0.06,

  /** Duration of the review-conversation modal alarm tone */
  REVIEW_ALARM_DURATION: 2,

  /** Interval between repeated alarm plays while the review card is unresolved (ms) */
  REVIEW_ALARM_REPEAT_INTERVAL_MS: 5000,

  /** Low/high note pair for the Alerted Conversation Alarm - single card ring (soft two-note chime) */
  ALERTS_ALARM_SINGLE_FREQ_LOW: 784,
  ALERTS_ALARM_SINGLE_FREQ_HIGH: 988,

  /** Duration of each note within the single-card chime (seconds) */
  ALERTS_ALARM_SINGLE_NOTE_DURATION: 0.18,

  /** Gap between the low and high note within a single-card chime pair (ms) */
  ALERTS_ALARM_SINGLE_NOTE_GAP_MS: 160,

  /** Interval between repeated chime pairs within the single-card ring pattern (ms) */
  ALERTS_ALARM_SINGLE_CHIME_INTERVAL_MS: 900,

  /** Low/high tone pair for the Alerted Conversation Alarm - multiple cards ring (alternating pulses) */
  ALERTS_ALARM_MULTIPLE_FREQ_LOW: 700,
  ALERTS_ALARM_MULTIPLE_FREQ_HIGH: 1100,

  /** Duration of each pulse within the multiple-cards ring pattern (seconds) */
  ALERTS_ALARM_MULTIPLE_PULSE_DURATION: 0.22,

  /** Interval between pulses in the multiple-cards ring pattern (ms) */
  ALERTS_ALARM_MULTIPLE_PULSE_INTERVAL_MS: 450,

  /** Fallback ring duration (seconds) used if the socket payload omits ring_seconds */
  ALERTS_ALARM_DEFAULT_RING_SECONDS: 10,

  /** Bounds applied to the socket-provided ring_seconds before it drives beep duration (seconds) */
  ALERTS_ALARM_MIN_RING_SECONDS: 1,
  ALERTS_ALARM_MAX_RING_SECONDS: 30,

  // ============================================
  // Timing Intervals (milliseconds)
  // ============================================
  
  /** Interval between beeps in new conversation sound */
  NEW_CONVERSATION_INTERVAL: 150,
  
  /** Interval between beeps in new message sound */
  NEW_MESSAGE_INTERVAL: 120,
  
  /** Interval between beeps in urgent sound (faster for urgency) */
  URGENT_INTERVAL: 80,
  
  /** Debounce interval for sound playing (prevents rapid-fire sounds) */
  SOUND_DEBOUNCE_INTERVAL_MS: 1000,
  
  /** Debounce interval for settings updates (prevents duplicate updates) */
  SETTINGS_UPDATE_DEBOUNCE_MS: 50,
  
  /** Maximum wait time for audio context resume (ms) */
  AUDIO_CONTEXT_RESUME_TIMEOUT: 100,
  
  // ============================================
  // Sound Counts
  // ============================================
  
  /** Number of beeps in urgent sound */
  URGENT_BEEP_COUNT: 3,
  
  /** Number of beeps in new message sound */
  NEW_MESSAGE_BEEP_COUNT: 2,
  
  // ============================================
  // Audio Context Recovery & Health Monitoring
  // ============================================
  
  /** Maximum number of recovery attempts before giving up */
  MAX_RECOVERY_ATTEMPTS: 5,
  
  /** Initial delay for recovery retry (ms) - exponential backoff starts here */
  RECOVERY_INITIAL_DELAY_MS: 100,
  
  /** Maximum delay between recovery attempts (ms) - caps exponential backoff */
  RECOVERY_MAX_DELAY_MS: 5000,
  
  /** Multiplier for exponential backoff (each retry doubles the delay) */
  RECOVERY_BACKOFF_MULTIPLIER: 2,
  
  /** Health check interval (ms) - how often to verify context is healthy */
  CONTEXT_HEALTH_CHECK_INTERVAL_MS: 30000, // 30 seconds
  
  /** Timeout for context resume operation (ms) */
  CONTEXT_RESUME_TIMEOUT_MS: 2000,
  
  /** Maximum consecutive failures before disabling audio */
  MAX_CONSECUTIVE_FAILURES: 3,
  
  /** Cooldown period after recovery failure (ms) - wait before retrying */
  RECOVERY_COOLDOWN_MS: 10000, // 10 seconds
};

// ============================================
// Sound Types - Type-safe sound identifiers
// ============================================

/**
 * Valid sound types for notifications.
 * Use these constants instead of string literals to prevent typos.
 */
export const SOUND_TYPES = {
  /** New message notification sound */
  NEW_MESSAGE: 'newMessage',
  /** New conversation notification sound */
  NEW_CONVERSATION: 'newConversation',
  /** Urgent alert notification sound */
  URGENT: 'urgent',
  /** Review-conversation modal alarm (bot auto-resolve card) */
  REVIEW_ALARM: 'reviewAlarm',
  /** Alerted Conversation Alarm - single unattended conversation ring */
  ALERTS_ALARM_SINGLE: 'alertsAlarmSingle',
  /** Alerted Conversation Alarm - multiple unattended conversations ring */
  ALERTS_ALARM_MULTIPLE: 'alertsAlarmMultiple'
};

/**
 * Valid tab names for sound settings.
 * Use these constants instead of string literals to prevent typos.
 */
export const SOUND_TAB_NAMES = {
  /** Alerts tab */
  ALERTS: 'alerts',
  /** Inbox tab */
  INBOX: 'inbox',
  /** My tab (assigned conversations) */
  MY: 'my',
  /** All conversations tab */
  ALL: 'all',
  /** Pinned conversations tab */
  PIN: 'pin'
};

// ============================================
// Type Validation Utilities
// ============================================

/**
 * Valid sound type values (for runtime validation)
 */
export const VALID_SOUND_TYPES = Object.values(SOUND_TYPES);

/**
 * Valid tab name values (for runtime validation)
 */
export const VALID_TAB_NAMES = Object.values(SOUND_TAB_NAMES);

/**
 * Validates a sound type and returns a valid type (with fallback).
 * @param {string} type - The sound type to validate
 * @param {string} fallback - Fallback type if validation fails (default: NEW_MESSAGE)
 * @returns {string} Valid sound type
 */
export const validateSoundType = (type, fallback = SOUND_TYPES.NEW_MESSAGE) => {
  if (!type || typeof type !== 'string') {
    console.warn(`Invalid sound type: ${type} (expected string), defaulting to ${fallback}`);
    return fallback;
  }
  
  if (!VALID_SOUND_TYPES.includes(type)) {
    console.warn(
      `Invalid sound type: "${type}". Valid types are: ${VALID_SOUND_TYPES.join(', ')}. Defaulting to ${fallback}`
    );
    return fallback;
  }
  
  return type;
};

/**
 * Validates a tab name and returns a valid tab name or null.
 * @param {string|null|undefined} tabName - The tab name to validate
 * @returns {string|null} Valid tab name or null if invalid/not provided
 */
export const validateTabName = (tabName) => {
  // null/undefined is valid (means no specific tab)
  if (tabName === null || tabName === undefined) {
    return null;
  }
  
  if (typeof tabName !== 'string') {
    console.warn(`Invalid tab name: ${tabName} (expected string or null), ignoring`);
    return null;
  }
  
  if (!VALID_TAB_NAMES.includes(tabName)) {
    console.warn(
      `Invalid tab name: "${tabName}". Valid tab names are: ${VALID_TAB_NAMES.join(', ')}. Ignoring`
    );
    return null;
  }
  
  return tabName;
};

